import { NavLink } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { api } from '../../lib/api.ts';
import { cx } from '../../components/ui.tsx';
import { Tooltip } from '../../components/Tooltip.tsx';
import { formatBytes } from '../../lib/display.ts';

/**
 * How full the system is, at the foot of the sidebar.
 *
 * It earns its place there by being the one number nobody thinks to go and
 * look for: a Drive fills up quietly, and the moment anybody wants to know is
 * the moment an upload has already failed. Google puts it in the same corner
 * for the same reason.
 *
 * Quiet on purpose — a thin bar and a line of grey text. It is a gauge, not a
 * warning, until it is nearly full, and then it says so in red rather than
 * getting louder in every other state.
 */

interface StorageUsage {
  disk: { capacity: string; free: string } | null;
  used: { total: string };
}

/** Where a bar stops being information and starts being a problem. */
const TIGHT = 0.9;

export function StorageMeter({ railed }: { railed: boolean }) {
  const query = useQuery({
    queryKey: ['storage'],
    queryFn: () => api.get<StorageUsage>('/api/storage'),
    // Storage moves slowly, and this renders on every page. Asking once a
    // minute is plenty and keeps the sidebar off the network.
    staleTime: 60_000,
  });

  if (!query.data) return null;

  const used = Number(query.data.used.total);
  const capacity = query.data.disk ? Number(query.data.disk.capacity) : 0;
  const fraction = capacity > 0 ? Math.min(1, used / capacity) : 0;
  const tight = fraction >= TIGHT;

  const line =
    capacity > 0
      ? `${formatBytes(used)} of ${formatBytes(capacity)} used`
      : `${formatBytes(used)} stored`;

  return (
    <Tooltip label="Storage" detail={line} side={railed ? 'right' : 'bottom'}>
      <NavLink
        to="/storage"
        className={({ isActive }) =>
          cx(
            'block rounded-lg py-2 transition-fast',
            railed ? 'px-0' : 'px-3',
            isActive ? 'bg-blue-50' : 'hover:bg-sunken',
          )
        }
      >
        <span
          className={cx(
            'flex items-center gap-2 text-body-sm',
            railed && 'justify-center',
            tight ? 'text-[var(--red-700,#b91c1c)]' : 'text-body',
          )}
        >
          <Cloud />
          {railed ? null : 'Storage'}
        </span>

        {railed ? null : (
          <>
            <span className="mt-1.5 block h-1.5 w-full overflow-hidden rounded-full bg-[var(--surface-sunken)]">
              <span
                className="block h-full rounded-full"
                style={{
                  width: `${Math.max(2, fraction * 100)}%`,
                  background: tight ? 'var(--red-600, #dc2626)' : 'var(--blue-600, #2563eb)',
                }}
              />
            </span>
            <span className="mt-1 block text-micro text-muted">{line}</span>
          </>
        )}
      </NavLink>
    </Tooltip>
  );
}

/** The same cloud the rest of the interface uses for stored things. */
function Cloud() {
  return (
    <svg width="18" height="18" viewBox="0 0 20 20" aria-hidden fill="currentColor">
      <path d="M14.5 16h-8a4 4 0 0 1-.6-7.96 5 5 0 0 1 9.64-1.1A3.75 3.75 0 0 1 14.5 16z" />
    </svg>
  );
}
